import { createFileRoute, useNavigate, Link } from '@tanstack/react-router'
import { RouteErrorFallback } from '@/components/ErrorBoundary'
import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { PageContainer, PageCard, PageHeader, PageHeaderContent, PageHeaderTitle, PageHeaderHeading, PageHeaderDescription, PageHeaderActions, PageContent } from '@/components/ui/page'
import { Badge } from '@/components/ui/badge'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Plus, Phone, Calendar, Loader2 } from 'lucide-react'
import { useAgents, type Agent } from '@/hooks/useAgents'
import { useCalendars } from '@/hooks/useCalendars'
import { usePhoneNumbers } from '@/hooks/usePhoneNumbers'
import { useVoices } from '@/hooks/useVoices'
import { TestCallDialog } from '@/components/agent/TestCallDialog'

export const Route = createFileRoute('/agent-configuration')({
  component: AgentConfiguration,
  errorComponent: RouteErrorFallback,
})

function AgentConfiguration() {
  const navigate = useNavigate()
  const [selectedAgent, setSelectedAgent] = useState<Agent | null>(null)
  const [testCallDialogOpen, setTestCallDialogOpen] = useState(false)
  const [agentSelectionDialogOpen, setAgentSelectionDialogOpen] = useState(false)

  // API hooks
  const { data: agents = [], isLoading: isLoadingAgents, error: agentsError } = useAgents()
  const { data: phoneNumbers = [], isLoading: isLoadingPhoneNumbers } = usePhoneNumbers()
  const { data: calendars = [], isLoading: isLoadingCalendars } = useCalendars()
  const { data: voices = [], isLoading: isLoadingVoices } = useVoices()

  // Handle card click to navigate to agent detail page
  const handleCardClick = (agent: Agent) => {
    navigate({ to: '/agent/$id', params: { id: agent.id } })
  }

  const handleTestCallClick = () => {
    if (agents.length === 0) {
      return // No agents available
    }

    if (agents.length === 1) {
      // If only one agent, use it directly
      setSelectedAgent(agents[0])
      setTestCallDialogOpen(true)
    } else {
      // If multiple agents, show selection dialog
      setAgentSelectionDialogOpen(true)
    }
  }

  const handleAgentSelection = (agent: Agent) => {
    setSelectedAgent(agent)
    setAgentSelectionDialogOpen(false)
    setTestCallDialogOpen(true)
  }

  // Show loading state for required data
  if (isLoadingAgents || isLoadingPhoneNumbers || isLoadingCalendars || isLoadingVoices) {
    return (
      <div className="p-8">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin" aria-hidden="true" />
          <span className="ml-2">Lade Daten…</span>
        </div>
      </div>
    )
  }

  return (
    <PageContainer padding="p-4 sm:p-8">
      <PageCard>
        <PageHeader>
          <PageHeaderContent>
            <PageHeaderTitle>
              <PageHeaderHeading>Agenten</PageHeaderHeading>
              <PageHeaderDescription>Konfigurieren Sie Ihre KI-Telefonassistenten</PageHeaderDescription>
            </PageHeaderTitle>
            <PageHeaderActions>
              {/* Der Link ist das Flex-Kind — ohne eigene Breite bliebe das `w-full`
                  des Buttons darin wirkungslos und die beiden Buttons ungleich breit. */}
              <Link to="/agent/new" className="w-full sm:w-auto">
                <Button className="w-full sm:w-auto">
                  <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
                  Agent hinzufügen
                </Button>
              </Link>
              <Button
                variant="outline"
                onClick={handleTestCallClick}
                disabled={agents.length === 0}
                className="w-full sm:w-auto"
              >
                <Phone className="mr-2 h-4 w-4" aria-hidden="true" />
                Agent testen
              </Button>
            </PageHeaderActions>
          </PageHeaderContent>
        </PageHeader>

        <PageContent className="p-6 overflow-y-auto">
        {isLoadingAgents ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin" aria-hidden="true" />
          <span className="ml-2">Lade Agenten…</span>
        </div>
      ) : agentsError ? (
        <div className="text-center py-8">
          <p className="text-red-500">Fehler beim Laden der Agenten</p>
        </div>
      ) : agents.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-muted-foreground">Keine Agenten vorhanden</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {agents.map(agent => {
            // Get phone number and calendar details for display
            const agentPhoneNumber = agent.phone_number_id ? phoneNumbers.find(p => p.id === agent.phone_number_id) : null
            const agentCalendars = calendars.filter(calendar =>
              agent.calendar_ids.includes(calendar.id)
            )

            return (
              <Card
                key={agent.id}
                className="hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => handleCardClick(agent)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    handleCardClick(agent)
                  }
                }}
                role="button"
                tabIndex={0}
              >
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <CardTitle className="text-xl">{agent.name}</CardTitle>
                      {agent.description && (
                        <p className="text-sm text-muted-foreground mt-1">
                          {agent.description}
                        </p>
                      )}
                    </div>
                    <Badge variant={agent.is_active ? 'default' : 'secondary'}>
                      {agent.is_active ? 'Aktiv' : 'Inaktiv'}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="text-xs text-muted-foreground">
                    <strong>Stimme:</strong> {(() => {
                      const voice = voices.find(v => v.id === agent.voice_id)
                      return voice ? voice.name : agent.voice_id || 'Keine zugewiesen'
                    })()}
                  </div>

                  <div>
                    <div className="flex items-center gap-2 mb-2 text-sm text-muted-foreground">
                      <Phone className="h-4 w-4" aria-hidden="true" />
                      <span>Telefonnummern ({agentPhoneNumber ? 1 : 0})</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {agentPhoneNumber ? (
                        <Badge key={agentPhoneNumber.id} variant="secondary" className="text-xs">
                          {agentPhoneNumber.number}
                        </Badge>
                      ) : (
                        <span className="text-xs text-muted-foreground italic">
                          Keine Telefonnummern zugewiesen
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div>
                    <div className="flex items-center gap-2 mb-2 text-sm text-muted-foreground">
                      <Calendar className="h-4 w-4" aria-hidden="true" />
                      <span>Kalender ({agentCalendars.length})</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {agentCalendars.length === 0 ? (
                        <span className="text-xs text-muted-foreground italic">
                          Keine Kalender zugewiesen
                        </span>
                      ) : (
                        agentCalendars.map(calendar => (
                          <Badge key={calendar.id} variant="secondary" className="text-xs">
                            {calendar.name}
                          </Badge>
                        ))
                      )}
                    </div>
                  </div>
                  
                  {agent.instructions && (
                    <div>
                      <div className="text-sm text-muted-foreground mb-1">
                        Anweisungen:
                      </div>
                      <p className="text-sm bg-muted/50 p-2 rounded text-muted-foreground">
                        {agent.instructions}
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}
        </PageContent>
      </PageCard>

      {/* Agent Selection Dialog */}
      <Dialog open={agentSelectionDialogOpen} onOpenChange={setAgentSelectionDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Agent auswählen</DialogTitle>
            <DialogDescription>
              Wählen Sie einen Agent für den Testanruf aus.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 py-4">
            {agents.map(agent => (
              <Button
                key={agent.id}
                variant="outline"
                className="w-full justify-start h-auto py-3 px-4"
                onClick={() => handleAgentSelection(agent)}
              >
                <div className="flex items-center gap-3 w-full">
                  <div className="flex-1 text-left">
                    <div className="font-medium">{agent.name}</div>
                  </div>
                  <Badge variant={agent.is_active ? 'default' : 'secondary'} className="shrink-0">
                    {agent.is_active ? 'Aktiv' : 'Inaktiv'}
                  </Badge>
                </div>
              </Button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Test Call Dialog */}
      {selectedAgent && (
        <TestCallDialog
          agentId={selectedAgent.id}
          agentName={selectedAgent.name}
          open={testCallDialogOpen}
          onOpenChange={setTestCallDialogOpen}
        />
      )}
    </PageContainer>
  )
}